/**
 * Wager races (P6, ADR-063) — the operator surface.
 *
 * List, create, edit (a SCHEDULED race only — a running race's terms are what
 * players are competing under; a settled one is a paid snapshot), void, and
 * the paid standings of a settled race. Nothing on this screen moves money:
 * settlement pays through the promo service's `race_prize` ledger path, and
 * voiding records a fact rather than deleting anything.
 */
import { useState, type ReactNode } from 'react';
import {
  createRace,
  fetchRaces,
  fetchRaceResults,
  RACE_METRICS,
  updateRace,
  voidRace,
  type RaceCreate,
  type RaceMetric,
  type RaceView,
} from '../api/races.ts';
import { useAsync } from '../lib/useAsync.ts';
import { absolute } from '../lib/datetime.ts';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { Badge, Card, EmptyState, ErrorState, Money, TableSkeleton } from '../ui/primitives.tsx';
import { IconTrend } from '../ui/icons.tsx';

const STATE_TONE: Record<RaceView['state'], 'info' | 'win' | 'neutral' | 'loss'> = {
  scheduled: 'info',
  running: 'win',
  settled: 'neutral',
  voided: 'loss',
};

const AMOUNT_RE = /^\d{1,12}(\.\d{1,4})?$/;

export function Races(): ReactNode {
  const races = useAsync(() => fetchRaces(), []);
  const [selected, setSelected] = useState<number | 'new' | null>(null);

  if (races.error) {
    return <ErrorState title="Races did not load" detail={races.error} onRetry={races.reload} />;
  }

  const rows = races.data ?? [];
  const current = selected === 'new' ? null : rows.find((r) => r.id === selected) ?? null;

  return (
    <div className="stack">
      <Card
        title="Wager races"
        icon={<IconTrend />}
        sub="Leaderboards over real wagering — prizes pay at settlement through the promo ledger, ranks recomputed from Postgres"
      >
        {races.loading ? (
          <TableSkeleton columns={['text', 'text', 'num', 'num']} rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            compact
            icon={<IconTrend size="lg" />}
            title="No races yet"
            hint="Create one — it runs itself: opens at start, settles at end, pays the prize schedule."
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Race</th>
                <th>State</th>
                <th>Metric</th>
                <th className="num">Prize pool</th>
                <th>Window</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td><Badge tone={STATE_TONE[r.state]}>{r.state}</Badge></td>
                  <td>{r.metric}</td>
                  <td className="num"><Money value={poolOf(r.prizes)} /></td>
                  <td className="num">
                    {absolute(r.startsAt)} → {absolute(r.endsAt)}
                  </td>
                  <td>
                    <button type="button" className="btn btn--sm btn--ghost" onClick={() => setSelected(r.id)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="row-actions row-actions--start">
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => setSelected('new')}>
            New race
          </button>
        </div>
      </Card>

      {selected === 'new' ? (
        <RaceEditor race={null} onDone={() => { setSelected(null); races.reload(); }} />
      ) : current ? (
        <>
          {current.state === 'scheduled' ? (
            <RaceEditor race={current} onDone={() => { setSelected(null); races.reload(); }} />
          ) : null}
          <RaceDetail race={current} onChanged={races.reload} />
        </>
      ) : null}
    </div>
  );
}

function poolOf(prizes: string[]): string {
  // Display-only sum of the INR prize schedule; 4dp strings, no float money —
  // the figures are small and exact-by-construction here (sum of exact 4dp).
  let paise = 0n;
  for (const p of prizes) {
    const [units = '0', frac = ''] = p.split('.');
    paise += BigInt(units) * 10000n + BigInt((frac + '0000').slice(0, 4));
  }
  const s = paise.toString().padStart(5, '0');
  return `${s.slice(0, -4)}.${s.slice(-4)}`;
}

function toLocalInput(iso: string): string {
  return iso.slice(0, 16);
}

function RaceEditor({ race, onDone }: { race: RaceView | null; onDone: () => void }): ReactNode {
  const [name, setName] = useState(race?.name ?? '');
  const [metric, setMetric] = useState<RaceMetric>((race?.metric as RaceMetric) ?? 'weighted_turnover');
  const [minWager, setMinWager] = useState(race?.minWager ?? '0');
  const [prizesText, setPrizesText] = useState((race?.prizes ?? ['5000', '2500', '1000']).join(', '));
  const [startsAt, setStartsAt] = useState(race ? toLocalInput(race.startsAt) : '');
  const [endsAt, setEndsAt] = useState(race ? toLocalInput(race.endsAt) : '');
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  const prizes = prizesText
    .split(',')
    .map((p) => p.trim())
    .filter((p) => p !== '');
  const prizesValid = prizes.length >= 1 && prizes.length <= 100 && prizes.every((p) => AMOUNT_RE.test(p));
  const windowValid = startsAt !== '' && endsAt !== '' && new Date(endsAt).getTime() > new Date(startsAt).getTime();
  const ready = name.trim().length >= 3 && AMOUNT_RE.test(minWager) && prizesValid && windowValid;

  async function onSave(): Promise<void> {
    if (!ready || busy) return;
    const payload: RaceCreate = {
      name: name.trim(),
      metric,
      minWager,
      prizes,
      startsAt: new Date(startsAt).toISOString(),
      endsAt: new Date(endsAt).toISOString(),
    };
    const result = await confirm({
      title: race ? 'Save race changes' : 'Create this race',
      body: race
        ? 'Only a scheduled race can be edited — once it starts, these are the terms players compete under.'
        : 'The race opens itself at the start time and settles itself at the end, paying the prize schedule through the promo ledger.',
      confirmLabel: race ? 'Save' : 'Create race',
      facts: [
        { label: 'Race', value: payload.name },
        { label: 'Metric', value: payload.metric },
        { label: 'Prizes', value: `${prizes.length} places · pool ₹${poolOf(prizes)}` },
        { label: 'Window', value: `${absolute(payload.startsAt)} → ${absolute(payload.endsAt)}` },
      ],
      note: { label: 'Reason', required: false },
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      if (race) await updateRace(race.id, payload);
      else await createRace(payload);
      toast(race ? 'Race updated.' : 'Race created.');
      onDone();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not save the race.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={race ? `Edit · ${race.name}` : 'New race'}
      icon={<IconTrend />}
      sub="Prizes are INR-authored, first place first. Minimum wager is the least total that places at all"
    >
      <label className="field">
        <span className="field__label">Name</span>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Weekend Turnover Sprint" />
      </label>
      <div className="row-actions row-actions--start">
        <label className="field">
          <span className="field__label">Metric</span>
          <select className="input input--sm" value={metric} onChange={(e) => setMetric(e.target.value as RaceMetric)}>
            {RACE_METRICS.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Min wager (INR)</span>
          <input className="input input--sm num" value={minWager} onChange={(e) => setMinWager(e.target.value.trim())} />
        </label>
        <label className="field">
          <span className="field__label">Starts</span>
          <input className="input input--sm" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Ends</span>
          <input className="input input--sm" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
        </label>
      </div>
      <label className="field">
        <span className="field__label">Prize schedule (INR, comma-separated, first place first)</span>
        <input className="input" value={prizesText} onChange={(e) => setPrizesText(e.target.value)} placeholder="5000, 2500, 1000" />
        {!prizesValid && prizesText.trim() !== '' ? (
          <span className="field__error">1–100 decimal amounts, e.g. 5000, 2500, 1000.</span>
        ) : (
          <span className="field__hint">{prizes.length} place{prizes.length === 1 ? '' : 's'} · pool ₹{prizesValid ? poolOf(prizes) : '—'}</span>
        )}
      </label>
      <div className="row-actions row-actions--start">
        <button type="button" className="btn btn--primary" disabled={busy || !ready} onClick={() => void onSave()}>
          {busy ? 'Saving…' : race ? 'Save changes' : 'Create race'}
        </button>
      </div>
    </Card>
  );
}

function RaceDetail({ race, onChanged }: { race: RaceView; onChanged: () => void }): ReactNode {
  const results = useAsync(
    () => (race.state === 'settled' ? fetchRaceResults(race.id) : Promise.resolve([])),
    [race.id, race.state],
  );
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const toast = useToast();

  async function onVoid(): Promise<void> {
    if (busy) return;
    const result = await confirm({
      title: 'Void this race',
      intent: 'danger',
      body: 'No results are computed and no prizes are paid. The race stays in the list as voided — a recorded fact, not a deletion.',
      confirmLabel: 'Void race',
      facts: [
        { label: 'Race', value: race.name },
        { label: 'State', value: race.state },
      ],
      note: { label: 'Reason', required: true },
    });
    if (!result.confirmed) return;
    setBusy(true);
    try {
      await voidRace(race.id);
      toast('Race voided.');
      onChanged();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'Could not void the race.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={`${race.name} · ${race.state}`}
      icon={<IconTrend />}
      sub={race.state === 'settled' ? 'The paid standings — where the money actually went' : 'Lifecycle'}
    >
      {race.state === 'scheduled' || race.state === 'running' ? (
        <div className="row-actions row-actions--start">
          <button type="button" className="btn btn--sm btn--danger" disabled={busy} onClick={() => void onVoid()}>
            Void race
          </button>
        </div>
      ) : null}

      {race.state === 'settled' ? (
        results.loading ? (
          <TableSkeleton columns={['num', 'num', 'num', 'num']} rows={3} />
        ) : (results.data ?? []).length === 0 ? (
          <p className="field__hint">Settled with no qualifying entrants — nothing was paid.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th className="num">Rank</th>
                <th className="num">Player</th>
                <th className="num">Score</th>
                <th className="num">Prize</th>
                <th>Currency</th>
              </tr>
            </thead>
            <tbody>
              {(results.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td className="num">{r.rank}</td>
                  <td className="num">#{r.userId}</td>
                  <td className="num"><Money value={r.score} /></td>
                  <td className="num"><Money value={r.prize} /></td>
                  <td>{r.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      ) : null}
    </Card>
  );
}
